import { useRouter } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"

import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import { dismissNewFarmSuccess } from "@/redux/user-farms.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AlertCard,
  AlertDialog,
  AlertDialogScrollView,
  AppText,
  Box,
  Button,
  View,
} from "@/theme"

import { FarmHqLogo } from "./FarmHqLogo"

import type { ButtonProps } from "@/theme"

import { StyleSheet } from "react-native"

/**
 * Closes new farm success dialog on press
 */
function ActionButton({
  description,
  onPress,
  text,
  ...props
}: Omit<ButtonProps, "children" | "onPress"> & {
  description: string
  onPress?: () => void
  text: string
}): React.JSX.Element {
  const dispatch = useRootDispatch()
  const handlePress = () => {
    if (onPress) {
      onPress()
    } else {
      dispatch(dismissNewFarmSuccess())
    }
  }
  return (
    <View>
      <Box my={"$2"}>
        <AppText colorScheme={"secondary"}>{description}</AppText>
      </Box>
      <Button onPress={handlePress} {...props} text={text} />
    </View>
  )
}

export function NewFarmSuccessDialog() {
  const dispatch = useRootDispatch()
  const isOpen = useRootSelector(
    (state) => state.userFarms.showNewFarmSuccess === true,
  )
  const { t } = useTranslation("noFarms")
  const withPermissions = usePermissionCheckedAction()
  const titleText: string = t("successTitle")
  const router = useRouter()

  const onClose = () => dispatch(dismissNewFarmSuccess())
  return (
    <AlertDialog
      id={"newFarmSuccess"}
      isOpen={isOpen}
      titleElement={titleText}
      IconComponent={
        <FarmHqLogo
          name={"farmhq-leaf-iso.png"}
          resizeMode={"contain"}
          style={styles.logo}
        />
      }
      onClose={onClose}
    >
      <AlertDialogScrollView>
        <AlertCard
          bodyText={t("successInstructions")}
          severity={"success"}
          titleText={t("welcomeToFarmHQ")}
        />
        <Box mb={"$4"}>
          <ActionButton
            IconComponent={"Add"}
            description={t("successAddDevices.description")}
            id={"addDeviceLink"}
            text={t("successAddDevices.text")}
            onPress={withPermissions({
              callback: () => {
                onClose()
                router.navigate("/devices/add-device")
              },
              required: "canManageDevicePermissions",
            })}
          />
        </Box>
        <Box mb={"$4"}>
          <ActionButton
            IconComponent={"Draw"}
            description={t("successDrawFields.description")}
            id={"createFieldLink"}
            text={t("successDrawFields.text")}
            onPress={withPermissions({
              callback: () => {
                onClose()
                router.navigate("/more/fields/create-field")
              },
              required: "canManageFields",
            })}
          />
        </Box>
        <ActionButton
          IconComponent={"Info"}
          description={t("successViewSupport.description")}
          id={"supportDocumentationLink"}
          text={t("successViewSupport.text")}
          onPress={() => {
            onClose()
            router.navigate("/more/support")
          }}
        />
      </AlertDialogScrollView>
    </AlertDialog>
  )
}
const styles = StyleSheet.create({
  logo: {
    height: 30,
    width: 30,
  },
})
